import React, { useState, useEffect, useRef } from 'react';
import FlipCountdown from './FlipCountdown';
import EventSpeakers from './EventSpeakers';
import EventDate from '../../shared/EventCard/parts/EventDate';
import EventMeta from '../../shared/EventCard/parts/EventMeta';
import ChapterRibbon from '../../shared/EventCard/ChapterRibbon';

function getDaysLabel(days_until) {
    if (days_until === 0) return 'Today';
    if (days_until === 1) return 'Tomorrow';
    if (days_until !== null && days_until !== undefined) return `In ${days_until} days`;
    return null;
}

// Featured layout: composes the shared parts (EventDate, EventMeta) and adds the
// featured-only chrome (badge, countdown, CTA, expandable description).
const FeaturedEventCard = ({ event, actionUrl, newTab = false }) => {
    const [descOpen, setDescOpen]           = useState(false);
    const [descTruncated, setDescTruncated] = useState(false);
    const descRef     = useRef(null);
    const descOpenRef = useRef(false);

    // Keep ref in sync so ResizeObserver always sees current open state.
    useEffect(() => { descOpenRef.current = descOpen; }, [descOpen]);

    useEffect(() => {
        if (!descRef.current) return;
        const el = descRef.current;
        const measure = () => {
            if (descOpenRef.current) return; // expanded — clientHeight unreliable
            setDescTruncated(el.scrollHeight > el.clientHeight + 1);
        };
        measure();
        let timer;
        const ro = new ResizeObserver(() => {
            clearTimeout(timer);
            timer = setTimeout(measure, 320);
        });
        ro.observe(el);
        return () => { clearTimeout(timer); ro.disconnect(); };
    }, [event.description]);

    const daysLabel  = getDaysLabel(event.days_until);
    const badgeLabel = daysLabel ? `Next event · ${daysLabel}` : 'Next event';

    const descClass = [
        'spf-desc-text',
        descTruncated && !descOpen ? 'spf-desc-text--truncated' : '',
        descOpen                   ? 'spf-desc-text--open'      : '',
    ].filter(Boolean).join(' ');

    return (
        <article className="sp-ocard sp-ocard--featured" style={{ cursor: 'default', width: '100%' }}>

            {/* ── Top strip: chapter ribbon (left) + Next event badge (right) ── */}
            <div className="spf-top">
                {event.chapter
                    ? <ChapterRibbon name={event.chapter.name} thumbnail={event.chapter.thumbnail} />
                    : <ChapterRibbon name="Workshop" icon="users" />}
                <div className="sp-ocard-top-row">
                    <span className="sp-ocard-badge sp-badge-next">{badgeLabel}</span>
                </div>
            </div>

            <div className="spf-body">

                {/* Date — flush-left full-height panel (outside the content padding). */}
                <EventDate date={event.date} />

                {/* ── Content column ── */}
                <div className="spf-main">
                    <div className="spf-head-info">
                        <span className="sp-ocard-title spf-title">{event.title}</span>
                        {event.subtitle && (
                            <span className="sp-ocard-subtitle spf-subtitle">{event.subtitle}</span>
                        )}
                        <EventSpeakers speakers={event.speakers} />
                        {event.description && (
                            <div className="spf-desc-inline">
                                <div
                                    ref={descRef}
                                    className={descClass}
                                    dangerouslySetInnerHTML={{ __html: event.description }}
                                />
                                {descTruncated && (
                                    <button className="spf-read-more" onClick={() => setDescOpen(o => !o)}>
                                        {descOpen ? 'Read less ▴' : 'Read more ▾'}
                                    </button>
                                )}
                            </div>
                        )}
                        <EventMeta event={event} />
                    </div>

                    {/* ── Footer: countdown (left) + Access (right) ── */}
                    <div className="spf-foot">
                        {event.start_utc && (
                            <FlipCountdown targetUtc={event.start_utc} onEnd={() => location.reload()} />
                        )}
                        <a href={actionUrl} {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})} className="sp-ocard-btn sp-ocard-btn-cta spf-cta">Access Event</a>
                    </div>
                </div>

            </div>

        </article>
    );
};

export default FeaturedEventCard;
